import { BLEND_MODES, Container, Graphics, Rectangle, Text, TextMetrics, TextStyle } from "pixi.js"

import { DISPLAY_FONT_FAMILY } from "../../text/typography"
import { BasicSprite } from "../../sprites/basic_sprite"
import { HELP_ENTRIES } from "../help_entries"
import { BLAZE, EMBER, EmberDrift, GOLD, Sparks, flicker, glowSprite } from "../ember_atmosphere"
import { easeOutBack, easeOutCubic } from "../../intro/logo_choreography"
import { BuilderPage, PageId } from "./page"
import { ACCENT, BODY_H, BODY_Y, CONTENT_W, RIM, TEXT, CARD_FILL, CARD_RIM } from "./theme"
import { capsText, drawGlassPanel, lensRule, sectionHeading, text } from "./widgets"

// The pages either side of the forge: the exile that puts you on the road, the briefing that sends
// you off, and the controls reference.

const TYPE_MS_PER_CHAR = 22

// A block of prose that types itself out. Pre-wrapped with TextMetrics so revealing a character at a
// time can't reflow the paragraph underneath itself (the usual word-wrap typewriter jitter). Lines are
// centred on their FINISHED width, so a line grows outward from where it will end up rather than
// sliding left as it fills.
class Typewriter extends Container {
    private lines: { label: Text; full: string; x: number }[] = []
    private caret = new Graphics()
    private total = 0
    private revealed = -1
    private elapsed: number
    private finished = false
    private readonly lineH: number

    constructor(
        body: string,
        width: number,
        { size = 17, color = TEXT, lineHeight = 27, startMs = 0 }: { size?: number; color?: number; lineHeight?: number; startMs?: number } = {},
    ) {
        super()
        this.lineH = lineHeight
        this.elapsed = startMs
        const style = new TextStyle({
            fontFamily: DISPLAY_FONT_FAMILY,
            fontSize: size,
            fontWeight: "400",
            wordWrap: true,
            wordWrapWidth: width,
            // Match what text() below actually renders, so the lines it measures are the lines drawn.
            strokeThickness: 0,
        })
        const measured = TextMetrics.measureText(body, style)
        measured.lines.forEach((line, i) => {
            const label = text("", { display: true, weight: "400", size, color })
            const full = line.trimEnd()
            const lineWidth = TextMetrics.measureText(full, style).width
            label.y = i * lineHeight
            this.addChild(label)
            this.lines.push({ label, full, x: Math.round((width - lineWidth) / 2) })
            this.total += full.length
        })
        this.caret.beginFill(GOLD, 0.9)
        this.caret.drawRect(0, 0, 2, size + 3)
        this.caret.endFill()
        this.addChild(this.caret)
        this.animate(0)
    }

    /** The block's height when fully typed -- known up front, so the panel can be sized to it. */
    get blockHeight(): number {
        return this.lines.length * this.lineH
    }

    get done(): boolean {
        return this.finished
    }

    get elapsedMs(): number {
        return this.elapsed
    }

    /** Type the rest at once -- the player clicked to hurry it along. */
    finish = () => {
        this.elapsed = Math.max(this.elapsed, this.total * TYPE_MS_PER_CHAR)
        this.animate(0)
    }

    animate(dtMs: number) {
        this.elapsed += dtMs
        const target = Math.min(this.total, Math.floor(this.elapsed / TYPE_MS_PER_CHAR))
        if (target !== this.revealed) {
            this.revealed = target
            let remaining = target
            this.lines.forEach(line => {
                const take = Math.max(0, Math.min(line.full.length, remaining))
                if (line.label.text.length !== take) {
                    line.label.text = line.full.slice(0, take)
                }
                line.label.x = line.x
                remaining -= line.full.length
            })
            this.finished = target >= this.total
        }

        // The caret rides the end of the line being typed, then blinks twice and goes.
        const typing = !this.finished
        const afterMs = this.elapsed - this.total * TYPE_MS_PER_CHAR
        this.caret.visible = typing || (afterMs < 1100 && Math.floor(afterMs / 275) % 2 === 1)
        const current =
            this.lines.find(line => line.label.text.length < line.full.length) || this.lines[this.lines.length - 1]
        if (current) {
            this.caret.x = current.x + current.label.width + 2
            this.caret.y = current.label.y + 3
        }
    }
}

/**
 * An imperial seal in red wax: a scalloped blob, a ring pressed into it, an eight-pointed star in the
 * middle, and a crescent of light on its upper left. Drawn about (0, 0).
 */
const drawSeal = (g: Graphics, r: number) => {
    const wax = 0x8e1b14
    const deep = 0x5a0e09
    const blob = (radius: number, wobble: number) => {
        const points: number[] = []
        for (let i = 0; i < 72; i++) {
            const angle = (i / 72) * Math.PI * 2
            const rr = radius + wobble * Math.cos(angle * 14) + wobble * 0.5 * Math.sin(angle * 5)
            points.push(Math.cos(angle) * rr, Math.sin(angle) * rr)
        }
        return points
    }
    g.beginFill(0x000000, 0.35)
    g.drawPolygon(blob(r, 1.4).map((v, i) => v + (i % 2 === 0 ? 2 : 3)))
    g.endFill()
    g.beginFill(wax, 1)
    g.drawPolygon(blob(r, 1.4))
    g.endFill()
    // Light on the upper left: a lighter disc with the body colour laid back over most of it.
    g.beginFill(0xc8402e, 0.55)
    g.drawCircle(-1.5, -1.5, r * 0.82)
    g.endFill()
    g.beginFill(wax, 1)
    g.drawCircle(0.8, 0.8, r * 0.8)
    g.endFill()
    // The impressed ring and star, darker -- pressed IN, with a hairline of light below each edge.
    g.lineStyle(1.6, deep, 0.95)
    g.drawCircle(0, 0, r * 0.62)
    g.lineStyle(1, 0xd0563f, 0.35)
    g.drawCircle(0, 0.9, r * 0.62)
    g.lineStyle(0)
    const star = (radius: number, inner: number, dy: number) => {
        const points: number[] = []
        for (let i = 0; i < 16; i++) {
            const angle = (i / 16) * Math.PI * 2 - Math.PI / 2
            const rr = i % 2 === 0 ? radius : inner
            points.push(Math.cos(angle) * rr, Math.sin(angle) * rr + dy)
        }
        return points
    }
    g.beginFill(0xd0563f, 0.35)
    g.drawPolygon(star(r * 0.42, r * 0.18, 0.9))
    g.endFill()
    g.beginFill(deep, 0.95)
    g.drawPolygon(star(r * 0.42, r * 0.18, 0))
    g.endFill()
}

const SEAL_R = 22
const SEAL_MS = 300

export class IntroPage extends BuilderPage {
    readonly pageId: PageId = "intro"
    readonly title = "The long exile"

    private typewriter?: Typewriter
    private seal?: Graphics
    private ring?: Graphics
    private panel?: Container
    private sparks = new Sparks()
    // Kept across repaints: a resize or a late font rebuilds the page, and the decree should carry on
    // from where it was rather than start typing over, or stamp itself a second time.
    private typedMs = 0
    private sealMs = -1
    private sealedAt?: number

    animate(dtMs: number) {
        this.sparks.update(dtMs)
        const typewriter = this.typewriter
        if (!typewriter) {
            return
        }
        // The decree waits for the page to arrive before it starts writing.
        if (!this.entering) {
            typewriter.animate(dtMs)
            this.typedMs = typewriter.elapsedMs
        }
        if (typewriter.done && this.sealMs < 0) {
            this.sealMs = 0
        }
        if (this.sealMs >= 0 && this.seal) {
            // A short pause after the last word, then the stamp comes down.
            this.sealMs += dtMs
            this.paintSeal(this.sealMs - 260)
        }
    }

    tearDown() {
        this.sparks.clear()
    }

    build() {
        this.wipe()
        this.sparks.clear()
        this.typewriter = undefined

        const panelW = 560 + (CONTENT_W - 780) * 0.5
        const panelX = Math.round((CONTENT_W - panelW) / 2)
        const pad = 34
        const innerW = panelW - pad * 2

        const panel = new Container()
        this.panel = panel
        this.addChild(panel)

        const kicker = capsText("By order of the Emperor", { role: "compactCaption", color: ACCENT, size: 11 })
        const body = new Typewriter(
            "You have been exiled to the barbarous lands of the West, for the Emperor will suffer no rivals in the East.",
            innerW,
            { startMs: this.typedMs },
        )
        // Laid out top-down from the panel's top edge, then the panel is sized to what it holds.
        const kickerTop = pad - 6
        const bodyTop = kickerTop + kicker.height + 24
        const sealCenter = bodyTop + body.blockHeight + 18 + SEAL_R
        const panelH = sealCenter + SEAL_R + pad - 8
        const panelY = Math.round(BODY_Y + (BODY_H - panelH) / 2)

        const glass = new Graphics()
        drawGlassPanel(glass, panelX, panelY, panelW, panelH, { radius: 12 })
        panel.addChild(glass)

        kicker.x = Math.round(panelX + (panelW - kicker.width) / 2)
        kicker.y = panelY + kickerTop
        panel.addChild(kicker)

        const rule = new Graphics()
        lensRule(rule, panelX + panelW / 2, kicker.y + kicker.height + 9, 70)
        panel.addChild(rule)

        body.x = panelX + pad
        body.y = panelY + bodyTop
        panel.addChild(body)
        this.typewriter = body

        // The seal, and the ring of light it throws when it lands. Pivoted on their centres so the
        // stamp scales down onto its own middle.
        const sealX = panelX + panelW / 2
        const sealY = panelY + sealCenter
        const ring = new Graphics()
        ring.position.set(sealX, sealY)
        panel.addChild(ring)
        this.ring = ring
        const seal = new Graphics()
        drawSeal(seal, SEAL_R)
        seal.position.set(sealX, sealY)
        panel.addChild(seal)
        this.seal = seal
        this.paintSeal(this.sealMs < 0 ? -1 : this.sealMs - 260)

        this.reveal(glass, 0, { rise: 14, ms: 480 })
        this.reveal(kicker, 160)
        this.reveal(rule, 220, { rise: 0 })

        // Clicking the decree hurries it: finish the words, and let the seal come straight down.
        this.interactive = true
        this.hitArea = new Rectangle(0, BODY_Y, CONTENT_W, BODY_H)
        this.cursor = "pointer"
        this.removeAllListeners("pointerdown")
        this.on("pointerdown", () => {
            this.finishEntrance()
            if (!this.typewriter?.done) {
                this.typewriter?.finish()
                this.typedMs = this.typewriter?.elapsedMs ?? this.typedMs
            }
        })

        this.host.setNavButtons([
            {
                title: "Forge your character ▸",
                primary: true,
                callback: () => this.host.switchPage("build"),
            },
        ])
    }

    /** The seal at `ms` into its fall: hidden before, stamping down, then at rest with a fading ring. */
    private paintSeal(ms: number) {
        const seal = this.seal
        const ring = this.ring
        if (!seal || !ring) {
            return
        }
        if (ms < 0) {
            seal.visible = false
            ring.clear()
            return
        }
        seal.visible = true
        const t = Math.min(1, ms / SEAL_MS)
        // Falls from above and large, overshoots flat, and settles: a press, not a fade.
        seal.scale.set(1.9 - 0.9 * easeOutBack(t, 1.1))
        seal.alpha = Math.min(1, t * 2.5)
        seal.rotation = -0.18 * (1 - easeOutCubic(t))

        if (t >= 1 && this.sealedAt === undefined) {
            this.sealedAt = ms
            this.sparks.throw(this.panel || this, seal.x, seal.y, 14, 90)
        }
        // The thump: the panel gives a little under the stamp, and a ring of light runs outward.
        const since = this.sealedAt === undefined ? -1 : ms - this.sealedAt
        if (this.panel) {
            this.panel.y = since >= 0 && since < 220 ? 2.5 * (1 - since / 220) * Math.cos(since / 22) : 0
        }
        ring.clear()
        if (since >= 0 && since < 650) {
            const k = since / 650
            ring.lineStyle(2 * (1 - k) + 0.5, GOLD, 0.55 * (1 - k))
            ring.drawCircle(0, 0, SEAL_R + 4 + 34 * easeOutCubic(k))
            ring.lineStyle(0)
        }
    }
}

// The loop of an ember camp, in the order a player meets it: the fire that keeps the Lord alive,
// the food that buys a household, and the assault that comes for both. These are the same three
// things the end-of-run scorecard counts, so the briefing and the reckoning agree.
const STEPS = [
    "Fell trees and feed the wood to the fire. Keep it burning.",
    "Hunt meat in the forest. Hungry strangers will be drawn in. Task them to aid in your survival.",
    "The dead come in waves. Keep the fire lit, and hold out for as long as you can.",
]

const STEP_FIRST = 300
const STEP_STAGGER = 170

export class JourneyPage extends BuilderPage {
    readonly pageId: PageId = "journey"
    readonly title = "Journey onward"

    private sparks = new Sparks()
    private campfire?: { halo: Container; embers: EmberDrift; glow: Container[] }
    private ms = 0

    animate(dtMs: number) {
        this.ms += dtMs
        this.sparks.update(dtMs)
        const fire = this.campfire
        if (fire) {
            fire.embers.update(dtMs)
            fire.glow.forEach((glow, i) => (glow.alpha = (i === 0 ? 0.42 : 0.3) * (1 + 0.16 * flicker(this.ms, i * 2.3))))
        }
    }

    tearDown() {
        this.sparks.clear()
    }

    build() {
        this.wipe()
        this.sparks.clear()

        const panelW = 470 + (CONTENT_W - 780) * 0.65
        const panelX = 24
        const panel = new Graphics()
        drawGlassPanel(panel, panelX, BODY_Y, panelW, BODY_H, { radius: 12 })
        this.addChild(panel)
        this.reveal(panel, 0, { rise: 12, ms: 460 })

        // Wrapped, and everything below it hung off its measured height: the lead is two lines of
        // prose rather than a caption, and an unwrapped one would run out over the campfire.
        const lead = text(
            "You are the steward of a great house. Your lord is dying. The fire is the only thing anchoring him to the world.",
            {
                role: "compactBody",
                color: TEXT,
                wrap: panelW - 44,
                display: true,
                size: 14,
            },
        )
        lead.x = panelX + 22
        lead.y = BODY_Y + 18
        this.addChild(lead)
        this.reveal(lead, 120)

        const heading = sectionHeading("Holding the camp", panelW - 44)
        heading.x = panelX + 22
        heading.y = lead.y + lead.height + 14
        this.addChild(heading)
        this.reveal(heading, 220, { rise: 4 })

        let y = heading.y + 22
        STEPS.forEach((step, i) => {
            const row = new Container()
            row.y = y
            this.addChild(row)

            const card = new Graphics()
            card.lineStyle(1, CARD_RIM, 0.6)
            card.beginFill(CARD_FILL, 0.85)
            card.drawRoundedRect(panelX + 22, 0, panelW - 44, 48, 8)
            card.endFill()
            card.lineStyle(0)
            row.addChild(card)

            // The numbered badge, and the flare it gives as its step arrives: each stop on the road
            // lighting up in turn.
            const flare = new Graphics()
            flare.beginFill(GOLD, 1)
            flare.drawCircle(panelX + 46, 24, 18)
            flare.endFill()
            flare.blendMode = BLEND_MODES.ADD
            flare.alpha = 0
            row.addChild(flare)
            const badge = new Graphics()
            badge.beginFill(RIM, 0.9)
            badge.drawCircle(panelX + 46, 24, 12)
            badge.endFill()
            row.addChild(badge)

            const number = text(`${i + 1}`, { role: "compactBody", color: 0x2a1c0c, bold: true })
            number.x = panelX + 46 - number.width / 2
            number.y = 24 - number.height / 2
            row.addChild(number)

            // Wrapped inside the card, to the right of its numbered badge; the vertical centring
            // below already works off the measured height, so a second line costs nothing.
            const body = text(step, { role: "compactLabel", color: TEXT, wrap: panelW - 112 })
            body.x = panelX + 68
            body.y = 24 - body.height / 2
            row.addChild(body)

            this.reveal(row, STEP_FIRST + i * STEP_STAGGER, {
                rise: 10,
                onFrame: t => (flare.alpha = 0.35 * Math.sin(Math.PI * Math.min(1, t * 1.15))),
            })

            y += 56
        })

        // The last word, stamped: large, then pressing into place with a few sparks off it.
        const closer = capsText("Survive", { role: "compactTitle", color: ACCENT, size: 20 })
        closer.pivot.set(closer.width / 2, closer.height / 2)
        closer.x = panelX + 22 + closer.width / 2
        closer.y = BODY_Y + BODY_H - 18 - closer.height / 2
        this.addChild(closer)
        this.reveal(closer, STEP_FIRST + STEPS.length * STEP_STAGGER + 220, {
            rise: 0,
            ms: 340,
            scaleFrom: 1.6,
            onLand: () => this.sparks.throw(this, closer.x, closer.y, 10, 80),
        })

        this.drawCampfire(panelX + panelW + 90 + (CONTENT_W - 780) * 0.175, BODY_Y + BODY_H / 2)
    }

    // The campfire the briefing is about, burning beside it: firelight pooled around it, breathing,
    // and embers lifting off into the dark.
    private drawCampfire(x: number, y: number) {
        const halo = new Container()
        const wide = glowSprite(BLAZE, 300, 260, 0.42)
        wide.position.set(x + 6, y + 10)
        const core = glowSprite(EMBER, 150, 130, 0.3)
        core.position.set(x + 6, y - 4)
        halo.addChild(wide, core)
        this.addChild(halo)

        const campfire = new BasicSprite({ name: "lpc-terrain:760", scale: 1.0, color: 0xffffff })
        const fire = new BasicSprite({ name: "fire", scale: 1.0, color: 0xffffff })
        fire.x = 3
        fire.y = -23

        const animation = new Graphics()
        animation.addChild(campfire)
        animation.addChild(fire)
        animation.scale.set(3.0)
        animation.x = x
        animation.y = y
        this.addChild(animation)

        const embers = new EmberDrift({
            width: 90,
            height: 210,
            count: 14,
            spawnBand: 0.12,
            minSize: 3,
            maxSize: 6,
            minRise: 26,
            maxRise: 52,
            sway: 10,
            minLife: 1.6,
            maxLife: 3.6,
        })
        embers.position.set(x + 6 - 45, y - 210)
        this.addChild(embers)

        this.campfire = { halo, embers, glow: [wide, core] }
        this.reveal(halo, 80, { rise: 0, ms: 700 })
        this.reveal(animation, 80, { rise: 6, ms: 520 })
    }
}

export class ControlsPage extends BuilderPage {
    readonly pageId: PageId = "controls"
    readonly title = "Controls"

    build() {
        this.wipe()

        const panelW = CONTENT_W - 140
        const panelX = (CONTENT_W - panelW) / 2
        const panel = new Graphics()
        drawGlassPanel(panel, panelX, BODY_Y, panelW, BODY_H, { radius: 12 })
        this.addChild(panel)
        this.reveal(panel, 0, { rise: 10, ms: 420 })

        // Two columns of entries, so nothing runs off the bottom of the panel.
        const colW = (panelW - 66) / 2
        const rows = Math.ceil(HELP_ENTRIES.length / 2)
        HELP_ENTRIES.forEach((entry, i) => {
            const col = Math.floor(i / rows)
            const row = i % rows
            const entryBox = new Container()
            entryBox.x = panelX + 24 + col * (colW + 18)
            entryBox.y = BODY_Y + 20 + row * 62
            this.addChild(entryBox)

            const title = text(entry.title, { role: "compactLabel", color: ACCENT, bold: true })
            entryBox.addChild(title)

            const body = text(entry.body, { role: "compactCaption", color: TEXT, wrap: colW })
            body.y = 16
            entryBox.addChild(body)

            this.reveal(entryBox, 90 + (row + col * 0.5) * 45, { rise: 6 })
        })

        this.host.setNavButtons([
            {
                title: "◂ Back",
                callback: () => {
                    this.host.resetNavButtons()
                    this.host.switchPage("journey")
                },
            },
        ])
    }
}
